// External Packages
import { useState, useEffect, useContext, Fragment, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";

// Internal Packages
import { AuthContext } from "../../context/auth-context";
import { DataContext } from "../../context/data-context";
import { toTitleCase } from "../../util/toTitleCase";
import { timestampToString } from "../../util/timestamp-to-date";
import { generateReceipt } from "../../util/generateReceipt";
import {
  deleteInvoice,
  fetchAllData,
  fetchCustomerById,
  fetchPaymentById,
} from "../../api/fetch";
import { chargeInvoice, createStripeInvoice, payInvoice, sendInvoiceLink, sendMessage } from "../../api/add";

// External Components

// Internal Components
import LoadingSpinnerWrapper from "../UI/LoadingSpinnerWrapper";
import DetailPair from "../UI/DetailPair";
import LinkPair from "../UI/LinkPair";
import Heading from "../UI/Heading";

export default function PaymentInfo() {
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  const { user } = useContext(AuthContext);
  const { fetchPayments } = useContext(DataContext);
  const navigate = useNavigate();

  const [payment, setPayment] = useState();
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState();
  const [showPayInvoice, setShowPayInvoice] = useState(false);
  const [warning, setWarning] = useState();

  // ============================================ FETCH PAYMENT DATA
  const fetchPaymentData = useCallback(() => {
    setLoading(true);
    fetchPaymentById(user, id)
      .then((data) => {
        setPayment(data);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  }, [user, id]);

  // ============================================ FETCH CUSTOMER
  const fetchCustomerHandler = async () => {
    try {
      setLoading(true);
      setWarning();
      const customer = await fetchCustomerById(user, payment.customer.id);
      setCustomer(customer);
      setLoading(false);
    } catch (err) {
      console.log(err);
      setWarning("Error fetching customer");
      setLoading(false);
    }
  };

  // ============================================ CHARGE INVOICE
  const chargeInvoiceHandler = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      const paymentMethodId = event.target.payment_method_id?.value;
      if (!paymentMethodId) throw new Error("No payment method selected");
      const charge = await chargeInvoice(user, {
        invoiceId: payment.id,
        paymentMethodId: paymentMethodId,
      });
      fetchAllData(() => {
        navigate("/payments");
        setCustomer();
        setLoading(false);
      });
    } catch (err) {
      setCustomer();
      setWarning(err.message);
      setLoading(false);
    }
  };

  // ============================================ MARK AS PAID
  const payInvoiceHandler = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      const source = event.target.source.value;
      const response = await payInvoice(user, {
        invoiceId: payment.id,
        source: source,
      });
      fetchAllData(() => {
        navigate("/payments");
        setLoading(false);
      });
    } catch (err) {
      setWarning(err.message);
      setLoading(false);
    }
  };

  // ============================================ DELETE INVOICE
  const deleteInvoiceHandler = async () => {
    try {
      setLoading(true);
      const response = await deleteInvoice(user, { paymentId: payment.id });
      fetchAllData(() => {
        navigate("/payments");
        setLoading(false);
      });
    } catch (err) {
      setWarning(err.message);
      console.log(err);
      setLoading(false);
    }
  };

  // ============================================ GENERATE RECEIPT
  const receiptHandler = () => {
    const receipt = {
      ...payment,
    };
    receipt.date_created = timestampToString(
      receipt.date_created,
      "MM/dd/yyyy"
    );
    receipt.due_date = timestampToString(receipt.due_date, "MM/dd/yyyy");
    receipt.booking.start_date = timestampToString(
      receipt.booking.start_date,
      "MM/dd/yyyy"
    );
    receipt.booking.end_date = timestampToString(
      receipt.booking.end_date,
      "MM/dd/yyyy"
    );
    receipt.status = payment.status === "succceeded" ? "Paid" : "Unpaid";
    receipt.amount = payment.amount.toString();
    receipt.subtotal = payment.subtotal.toString();
    receipt.taxes = (payment.amount - payment.subtotal).toString();
    generateReceipt(receipt, payment.customer);
  };

   // ============================================ CREATE STRIPE INVOICE
   const stripeInvoiceHandler = async () => {
    try {
      setLoading(true);
      const response = await createStripeInvoice(user, { invoiceId: payment.id });
      window.open(response.link, "_blank");
      fetchPaymentData();
      setLoading(false);
    } catch (err) {
      setWarning(err.message);
      console.log(err);
      setLoading(false);
    }
  };

   // ============================================ SEND PAYMENT REMINDER
   const sendReminderHandler = async () => {
    try {
      setLoading(true);
      const message = `Your invoice of $${
        payment.amount
      } for "${payment.description}" is due on ${timestampToString(
        payment.due_date,
        "eee dd MMM, yyyy"
      )}. Please make the payment at your earliest convenience. Thank you!`;
      const response = await sendMessage(user, {
        customerId: customer.id,
        message: message,
      });
      setLoading(false);
    } catch (err) {
      setWarning(err.message);
      console.log(err);
      setLoading(false);
    }
  };

  // ============================================ USE_EFFECT: FETCH PAYMENT DATA
  useEffect(() => {
    fetchPaymentData();
  }, [fetchPaymentData]);

  // ============================================ DEFAULT MENU
  const defaultMenu = () => {
    const paid = payment.status === "succeeded";
    const invoice = payment.type === "invoice";
    return (
      <>
        {!paid && <button
          onClick={() => setShowPayInvoice(true)}
          className="btn-secondary"
        >
          Mark as Paid
        </button>}
        {invoice && <button onClick={deleteInvoiceHandler} className="btn-secondary">
          Delete Invoice
        </button>}
        {!paid && <button onClick={fetchCustomerHandler} className="btn-secondary">
          Charge Customer
        </button>}
        {invoice && !paid && !payment.stripe && <button onClick={stripeInvoiceHandler} className="btn-secondary">
          Generate Payment Link
        </button>}
        {invoice && !paid && <button onClick={sendReminderHandler} className="btn-secondary">
          Send Reminder
        </button>}
        <button onClick={receiptHandler} className="btn-secondary">
          Download PDF
        </button>
        {warning && <p className="text-red-500">{warning}</p>}
      </>
    );
  };

  // ============================================ CHARGE MENU
  const chargeCustomerMenu = () => {
    return (
      <form
        onSubmit={chargeInvoiceHandler}
        className="grid gap-6 md:grid-cols-3 md:col-span-3"
      >
        <button onClick={() => setCustomer()} className="btn-secondary">
          Cancel
        </button>
        {customer?.default_payment_method ? (
          <select className="form-input" name="payment_method_id">
            <option value={customer.default_payment_method.id}>
              {customer.default_payment_method.brand}{" "}
              {customer.default_payment_method.last4}
            </option>
            {customer.payment_methods &&
              customer.payment_methods.map((pm) => (
                <option value={pm.id}>
                  {pm.brand} {pm.last4}
                </option>
              ))}
          </select>
        ) : (
          <input
            className="form-input-disabled"
            placeholder="No Payment Methods Attached"
            disabled
          />
        )}
        <button type="submit" className="btn-primary">
          Confirm
        </button>
      </form>
    );
  };

  // ============================================ MARK AS PAID MENU
  const payInvoiceMenu = () => {
    return (
      <form
        onSubmit={payInvoiceHandler}
        className="grid gap-6 md:grid-cols-3 md:col-span-3"
      >
        <button
          onClick={() => setShowPayInvoice(false)}
          className="btn-secondary"
        >
          Cancel
        </button>
        <div className="grid">
          <input
            className="form-input"
            placeholder="Source e.g. Zelle, Cashapp etc"
            name="source"
            required
          />
        </div>
        <button type="submit" className="btn-primary">
          Confirm
        </button>
      </form>
    );
  };

  // ============================================ RENDER MENU
  const renderMenu = () => {
    if (customer) return chargeCustomerMenu();
    if (showPayInvoice) return payInvoiceMenu();
    else return defaultMenu();
  };

  if (!id)
    return (
      <div className="w-full h-full p-8 rounded-lg bg-stone-900">
        <p className="text-stone-400">Payment ID Not Provided!</p>
      </div>
    );

  if (!loading && !payment)
    return (
      <div className="w-full h-full p-8 rounded-lg bg-stone-900">
        <p className="text-stone-400">Payment Not Found!</p>
      </div>
    );

  return (
    <div className="container">
      <Heading title="Payment Details" />

      {loading ? (
        <LoadingSpinnerWrapper />
      ) : (
        <div className="info">
          <DetailPair label="Payment ID" value={payment.id} />
          <DetailPair label="Type" value={toTitleCase(payment.type)} />
          <DetailPair
            label="Date Created"
            value={timestampToString(
              payment.date_created,
              "eee dd MMM, yyyy - hh:mm a"
            )}
          />

          {payment.type === "invoice" && (
            <Fragment>
              <DetailPair label="Status" value={toTitleCase(payment.status)} />
              <DetailPair
                label="Due Date"
                value={`${
                  payment.due_date
                    ? timestampToString(
                        payment.due_date,
                        "eee dd MMM, yyyy - hh:mm a"
                      )
                    : "-"
                }`}
              />
              <DetailPair
                label="Date Paid"
                value={`${
                  payment.date_paid
                    ? timestampToString(
                        payment.date_paid,
                        "eee dd MMM, yyyy - hh:mm a"
                      )
                    : "-"
                }`}
              />
              <DetailPair
                label="Paid via"
                value={payment.source ? payment.source : payment.stripeId}
              />
            </Fragment>
          )}

          <DetailPair
            label="Customer ID"
            value={payment.customer.id}
            link={`/customers/info?id=${payment.customer.id}`}
          />
          <DetailPair
            label="Customer"
            value={payment.customer.firstName + " " + payment.customer.lastName}
            link={`/customers/info?id=${payment.customer.id}`}
          />

          <DetailPair
            label="Booking ID"
            value={payment.booking?.id || "-"}
            link={`/bookings/info?id=${payment.booking?.id}` || ""}
          />
          {payment.stripe && <LinkPair label="Stripe Payment" link={payment.stripe.link} value="Link" />}
          <DetailPair label="Amount" value={"$" + payment.amount.toString()} />
          <div className="md:col-span-2">
            <DetailPair
              label="Description"
              value={toTitleCase(payment.description)}
            />
          </div>

          <hr className="hr md:col-span-3" />

          {renderMenu()}
        </div>
      )}
    </div>
  );
}
